import React, { useContext } from 'react'

import { getValueFormat } from '@baurine/grafana-value-formats'
import { Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { RemoteNamespace } from '~/translations'

import {
  OrgAndClusterContext,
  TimeRangeContext,
  OrgAndClusterType,
  OrgType
} from '../shared/context'

export const DescriptionColumn: React.FC<{
  k: string
  vars: any
  diff: number
}> = ({ k, vars, diff }) => {
  const { t } = useTranslation(RemoteNamespace.MetricsCompare)
  return diff > 0.1 || diff < -0.1 ? <span>{t(k, vars)}</span> : <></>
}

export const DiffColumn: React.FC<{ value: number }> = ({ value }) => {
  return value >= 1e10 || value <= -1e10 ? (
    <span>Infinity</span>
  ) : (
    <span>{getValueFormat('percentunit')(value, 2)}</span>
  )
}

export const T1T2LinkColumn: React.FC<{ dataIndex: string }> = ({
  dataIndex
}) => {
  const { t1StartTime, t1EndTime, t2StartTime, t2EndTime } =
    useContext(TimeRangeContext)!
  const orgAndClusterContext = useContext(OrgAndClusterContext)
  const { isLoading } = orgAndClusterContext
  const haveLink = linkKeyMap[dataIndex]

  return !!haveLink ? (
    <>
      <Typography.Link
        href={haveLink(orgAndClusterContext, [
          t1StartTime! * 1000,
          t1EndTime! * 1000
        ])}
        target='_blank'
        disabled={isLoading}
      >
        T1
      </Typography.Link>{' '}
      <Typography.Link
        href={haveLink(orgAndClusterContext, [
          t2StartTime! * 1000,
          t2EndTime! * 1000
        ])}
        target='_blank'
        disabled={isLoading}
      >
        T2
      </Typography.Link>
    </>
  ) : (
    <></>
  )
}

type Linker = (
  context: OrgAndClusterType,
  timeRange: [number, number]
) => string

const grafanaLinker = (subPath: { cloud: string; op: string }): Linker => {
  return (context, timeRange) => {
    const { clusterId, orgId, orgType } = context
    return getGrafanaLink(
      subPath[orgType!],
      orgType!,
      timeRange,
      clusterId!,
      orgId!
    )
  }
}

export const getGrafanaLink = (
  grafanaId: string,
  orgType: OrgType,
  timeRange: [number, number],
  clusterId: string,
  orgId: string
): string => {
  return `${window.location.protocol}//${window.location.host}/grafana/d/${grafanaId}?orgId=1&var-tidb_cluster=${clusterId}&var-org=${orgId}&from=${timeRange[0]}&to=${timeRange[1]}&var-org_type=${orgType}`
}

// const kibanaLinker = (
//   subPath: { cloud: string; op: string },
//   filters: string = ''
// ): Linker => {
//   return (context, timeRange) => {
//     const { clusterId, orgId, orgType } = context
//     const t1 = new Date(timeRange[0]).toISOString()
//     const t2 = new Date(timeRange[1]).toISOString()
//     return orgType === 'cloud'
//       ? `${getLogsBasePath()}/app/discover?security_tenant=global#/?_a=(columns:!(component,log_file,level,message),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,key:user_cluster_id,negate:!f,params:(query:'${clusterId}'),type:phrase),query:(match_phrase:(user_cluster_id:'${clusterId}'))),${filters}),interval:auto,query:(language:kuery,query:''),sort:!())&_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:'${t1}',to:'${t2}'))`
//       : `${window.location.protocol}//${window.location.host}/kibana/app/discover?security_tenant=tenant${orgId}#/?_a=(columns:!(component,log_file,level,message),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,key:cluster_id,negate:!f,params:(query:'${clusterId}'),type:phrase),query:(match_phrase:(cluster_id:'${clusterId}'))),${filters}),interval:auto,query:(language:kuery,query:''),sort:!())&_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:'${t1}',to:'${t2}'))`
//   }
// }

const grafanaLinks: { [key: string]: Linker } = {
  performanceOverview: grafanaLinker({
    cloud: 'eDbRZpnWatidbcloudmaster/performance-overview-cluster-clinic',
    op: 'eDbRZpnWaclustermaster/performance-overview-cluster-clinic'
  }),
  tidbDetails: grafanaLinker({
    cloud: '000000011tidbcloudmaster/tidb-cluster-clinic',
    op: '000000011clustermaster/tidb-cluster-clinic'
  }),
  tikvDetails: grafanaLinker({
    cloud: 'RDVQiEzZztidbcloudmaster/tikv-details-cluster-clinic',
    op: 'RDVQiEzZzclustermaster/tikv-details-cluster-clinic'
  }),
  pdDetails: grafanaLinker({
    cloud: 'Q6RuHYIWktidbcloudmaster/pd-cluster-clinic',
    op: 'Q6RuHYIWkclustermaster/pd-cluster-clinic'
  }),
  tiflashDetails: grafanaLinker({
    cloud: 'kWxNAVnGztidbcloudmaster/tiflash-proxy-details-cluster-clinic',
    op: 'kWxNAVnGzclustermaster/tiflash-proxy-details-cluster-clinic'
  })
}

const linkKeyMap: { [key: string]: Linker } = {
  QPS: grafanaLinks.performanceOverview,
  Connnection_count: grafanaLinks.performanceOverview,
  Active_connection_count: grafanaLinks.performanceOverview,
  CPS: grafanaLinks.performanceOverview,
  Database_time: grafanaLinks.performanceOverview,
  Avg_cluster_duration: grafanaLinks.performanceOverview,
  P99_cluster_duration: grafanaLinks.performanceOverview,
  TiDB_CPU_max: grafanaLinks.tidbDetails,
  TiKV_CPU_max: grafanaLinks.tikvDetails,
  TiKV_IO_MBps_max: grafanaLinks.tikvDetails,
  TiKV_disk_usage_max: grafanaLinks.tikvDetails,
  Plan_cache_OPS: grafanaLinks.performanceOverview,
  Disconnection_count: grafanaLinks.tidbDetails,
  QPS_failed: grafanaLinks.performanceOverview,
  Get_token_time: grafanaLinks.performanceOverview,
  Parse_time: grafanaLinks.performanceOverview,
  Compile_time: grafanaLinks.performanceOverview,
  Execute_time: grafanaLinks.performanceOverview,
  TiDB_memory_delta: grafanaLinks.tidbDetails,
  TiDB_memory_max: grafanaLinks.tidbDetails,
  TiDB_CPU_delta: grafanaLinks.tidbDetails,
  TiKV_memory_delta: grafanaLinks.tikvDetails,
  TiKV_memory_max: grafanaLinks.tikvDetails,
  TiKV_CPU_delta: grafanaLinks.tikvDetails,
  TiKV_IO_MBps_delta: grafanaLinks.tikvDetails,
  TiKV_disk_usage_delta: grafanaLinks.tikvDetails,
  PD_CPU_usage_delta: grafanaLinks.pdDetails,
  PD_CPU_usage_max: grafanaLinks.pdDetails,
  // Error_log_count: kibanaLinker(
  //   { cloud: '', op: '' },
  //   "('$state':(store:appState),meta:(alias:!n,disabled:!f,key:component,negate:!f,params:!(tikv,tidb,pd),type:phrases,value:'tikv,%20tidb,%20pd'),query:(bool:(minimum_should_match:1,should:!((match_phrase:(component:tikv)),(match_phrase:(component:tidb)),(match_phrase:(component:pd)))))),('$state':(store:appState),meta:(alias:!n,disabled:!f,key:level,negate:!f,params:(query:ERROR),type:phrase),query:(match_phrase:(level:ERROR)))"
  // ),
  TiFlash_memory_delta: grafanaLinks.tiflashDetails,
  TiFlash_memory_max: grafanaLinks.tiflashDetails,
  TiFlash_CPU_delta: grafanaLinks.tiflashDetails,
  TiFlash_CPU_max: grafanaLinks.tiflashDetails,
  TiFlash_IO_MBps_delta: grafanaLinks.tiflashDetails,
  TiFlash_IO_MBps_max: grafanaLinks.tiflashDetails,
  TiFlash_disk_usage_delta: grafanaLinks.tiflashDetails,
  TiFlash_disk_usage_max: grafanaLinks.tiflashDetails
}
